import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {decamelizeKeys} from 'humps';

import {operationsService} from 'api/operations';
import {tasksService} from 'api/tasks';
import {tasksStore} from 'IocContainer';
import {getTaskChangedFieldsOnly, isChangedTaskAssigneesList, replaceOwnerWithAssignee} from 'shared/helpers/task';
import {toTaskModelRawDTO} from 'shared/mapping/task';
import {TaskObjectType} from 'shared/models/task/const';
import {TaskAssignees} from 'shared/models/task/member';
import {OperationModelRawDTO, TaskDetailsModelDTO} from 'shared/models/task/task';
import {expandDates, pickOperationDateFields} from 'shared/prediction/date-expander';
import {getMemoizedCalendar} from 'shared/prediction/utils';
import {RootState} from 'store';
import {selectProjectById} from 'store/projects';
import {serializeError} from 'store/utils/serializeError';
import {isInstanceOfOperation} from 'modules/Tasks/components/Gantt/utils/gantt';

type UpdateOrCreateTaskPayload = {
  model: TaskDetailsModelDTO;
  currentTask?: TaskDetailsModelDTO;
  membersToAdd?: TaskAssignees[];
  members?: TaskAssignees[];
  isScopedUpdate?: boolean;
};

const addTaskAssignees = async (
  taskId: string,
  {membersToAdd}: Pick<UpdateOrCreateTaskPayload, 'membersToAdd'> = {},
) => {
  const promises = [];
  if (membersToAdd?.length) {
    membersToAdd.forEach((member) => {
      promises.push(tasksService.addAssignee(taskId, member));
    });
    await Promise.all<TaskDetailsModelDTO[]>(promises);
  }
  if (!promises.length) return;
  const actual = (await promises.pop()) as TaskDetailsModelDTO;
  return actual.assignees;
};

export const updateTaskAssignees = async (
  taskId: string,
  {members}: Pick<UpdateOrCreateTaskPayload, 'members'> = {},
) => {
  return tasksService.updateAssignee(taskId, members);
};

export const getTask = createAsyncThunk(
  '/tasks/getTask',
  (taskId: string) => {
    const maybeOperation = tasksStore.getTaskById(taskId);
    if (isInstanceOfOperation(maybeOperation)) {
      return operationsService.getOperation(maybeOperation.project_id, taskId);
    }
    return tasksService.getTask(taskId);
  },
  {serializeError},
);

export const updateTask = createAsyncThunk<TaskDetailsModelDTO, UpdateOrCreateTaskPayload, {state: RootState}>(
  '/tasks/updateTask',
  async ({model, currentTask, members, isScopedUpdate}: UpdateOrCreateTaskPayload, {rejectWithValue, getState}) => {
    const changedFields = getTaskChangedFieldsOnly(currentTask, model);
    const hasChangedFields = !!Object.keys(changedFields).length;
    if (isChangedTaskAssigneesList(replaceOwnerWithAssignee(currentTask.assignees), [...Object.values(members)])) {
      const res = await updateTaskAssignees(currentTask.id, {members});
      if (!hasChangedFields) return res;
    }
    try {
      if (hasChangedFields) {
        if (currentTask.objectType === TaskObjectType.operation) {
          const rawOp = toTaskModelRawDTO(currentTask) as OperationModelRawDTO;
          const delta = getTaskChangedFieldsOnly(currentTask, model);
          const changedFields = decamelizeKeys(delta);
          const dateFields = pickOperationDateFields(changedFields);
          const state = getState();
          const proj = selectProjectById(state, tasksStore.projectId);
          const calendar = getMemoizedCalendar(proj);
          const dateUpdates = expandDates(calendar, rawOp, dateFields)[currentTask.id];
          const operation = await operationsService.updateOperation(
            currentTask.id,
            tasksStore.projectId,
            {
              ...changedFields,
              ...dateUpdates,
            },
            false,
          );
          return operation as unknown as TaskDetailsModelDTO;
        }
        return await tasksService.updateTask(getTaskChangedFieldsOnly(currentTask, model), isScopedUpdate);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {serializeError},
);

export const createTask = createAsyncThunk(
  '/tasks/createTask',
  async ({model, ...members}: UpdateOrCreateTaskPayload, api) => {
    try {
      const res = await tasksService.createTask(model);
      const newAssignees = await addTaskAssignees(res.id, members);
      return newAssignees.length
        ? ({...res, assignees: newAssignees, assignmentCount: newAssignees.length} as TaskDetailsModelDTO)
        : res;
    } catch (error) {
      return api.rejectWithValue(error);
    }
  },
  {serializeError},
);

type DeleteTaskParams = {
  taskId: string;
  isOperation: boolean;
  projectId: string;
};
export const deleteTask = createAsyncThunk(
  '/tasks/delete',
  async ({taskId, isOperation = false, projectId}: DeleteTaskParams) => {
    if (isOperation) {
      return operationsService.deleteOperation(projectId, taskId);
    }
    return tasksService.deleteTask(taskId);
  },
  {serializeError},
);

export const updateAppliedBaselineDate = createAction<{appliedBaselineDate: string}>(
  '/tasks/updateAppliedBaselineDate',
);

export const restoreTask = createAsyncThunk('/tasks/restore', async (taskId: string) => {
  return tasksService.restoreTask(taskId);
});
